import WithdrawalsAnalysisService from "@services/resources/WithdrawalsAnalysisService";
import WithdrawService from '@services/resources/WithdrawService';
import Vue from 'vue';

const service = WithdrawalsAnalysisService.build();
const historyService = WithdrawService.build();

const initialIndicatorCard = () => ({
  type: '',
  duplicate: '',
  tooltip: '',
  filter: '',
  icon: null,
  title: '',
  sales: null,
  value: null,
  selected: false,
});

const initialSellerCard = () => ({
  path: '',
  key: '',
  second_key: '',
  third_key: '',
  icon: null,
  title: '',
  type: '',
  filter: ''
});

const initialSaleHistoryFilters = () => ({
  id: '',
  email: '',
  name: '',
  phone: '',
  document: '',
  indicatorCard: '',
  sellerCard: '',
  badge: ''
});

const initialFilters = () => ({
  name: '',
  email: '',
  document: '',
  status: null,
  currencyId: 1,
  analyst_name: ''
});

const state = {
  withdrawals: [],
  pagination: {
    currentPage: 1,
    perPage: 5,
    totalRows: 0
  },
  filters: initialFilters(),
  loading: false,
  loadingHistory: false,
  userId: null,
  withdrawalHistory: [],
  // Kept apart from the general `error`: the timeline must tell a failure from an empty history.
  historyError: false,
  pollingInterval: null,
  lastPollingTime: null,
  withdrawalGroups: null,
  withdrawalGroupData: [],
  indicatorCard: initialIndicatorCard(),
  sellerCard: initialSellerCard(),
  saleHistoryFilters: initialSaleHistoryFilters(),
  reviewWithdrawalTab: 'seller',
  activeIndicatorCardIds: [],
  activeFilterCardIds: [],
  groups: [],
  groupPagination: {
    currentPage: 1,
    perPage:     5,
    totalRows:   0,
    lastPage:    1,
    hasMore:     false,
  },
  groupLoading: false,
  groupError: null,
  withdrawalItemsByDate: {},
  withdrawalItemsLoading: false,
  withdrawalItemsError: null,
  withdrawalsPaginated:[],
  loadingWithdrawalsAnalysis: false,
  errorMessage: "",
  error: "",
  statusOptions: []
}

const getters = {
  withdrawals: state => state.withdrawals,
  pagination: state => state.pagination,
  filters: state => state.filters,
  loading: state => state.loading,
  loadingHistory: state => state.loadingHistory,
  errorMessage: state => state.errorMessage,
  error: state => state.error,
  userId: state => state.userId,
  withdrawalHistory: state => state.withdrawalHistory,
  withdrawalHistoryError: state => state.historyError,
  withdrawalGroups: state => state.withdrawalGroups || { date: null, withdrawals: [] },
  withdrawalGroupData: state => state.withdrawalGroupData || [],
  getIndicatorCard: state => state.indicatorCard,
  getSellerCard: state => state.sellerCard,
  getSaleHistoryFilters: state => state.saleHistoryFilters,
  getReviewWithdrawalTab: state => state.reviewWithdrawalTab,
  getActiveIndicatorCardIds: state => state.activeIndicatorCardIds,
  getActiveFilterCardIds: state => state.activeFilterCardIds,
  groups: state => state.groups,
  groupLoading: state => state.groupLoading,
  groupError: state => state.groupError,
  groupsPagination: state => state.groupPagination,

  withdrawalItemsByDate: state => date =>
    state.withdrawalItemsByDate[date] || [],
  withdrawalItemsLoading: state => state.withdrawalItemsLoading,
  withdrawalItemsError:  state => state.withdrawalItemsError,
  withdrawalsPaginated: state => state.withdrawalsPaginated,
  statusOptions: state => state.statusOptions,
}

const mutations = {
  SET_WITHDRAWALS(state, withdrawals) {
    state.withdrawals = withdrawals;
  },

  SET_PAGINATION(state, payload) {
    state.pagination = { ...state.pagination, ...payload };
  },

  SET_FILTER(state, { filter, value }) {
    state.filters[filter] = value;
  },

  RESET_WITHDRAWAL_FILTERS(state) {
    state.filters = initialFilters();
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_LOADING_HISTORY(state, loadingHistory) {
    state.loadingHistory = loadingHistory;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_USER_ID(state, userId) {
    state.userId = userId;
  },

  SET_WITHDRAWAL_HISTORY(state, history) {
    state.withdrawalHistory = history || [];
  },
  SET_WITHDRAWAL_HISTORY_ERROR(state, failed) {
    state.historyError = failed;
  },

  setWithdrawalSales(state, sales) {
    state.withdrawalSales = sales;
  },

  updateWithdrawal(state, { id, status, operator_id, updated_at }) {
    const groupIndex = state.withdrawals.findIndex(group =>
      group.withdrawals.some(withdrawal => withdrawal.id === id)
    );

    if (groupIndex !== -1) {
      const group = state.withdrawals[groupIndex];
      const withdrawalIndex = group.withdrawals.findIndex(withdrawal => withdrawal.id === id);

      if (withdrawalIndex !== -1) {
        const updatedWithdrawal = {
          ...group.withdrawals[withdrawalIndex],
          status,
          operator_id,
          updated_at,
        };

        Vue.set(state.withdrawals[groupIndex].withdrawals, withdrawalIndex, updatedWithdrawal);

      }
    }
  },

  SET_POLLING_INTERVAL(state, interval) {
    state.pollingInterval = interval;
  },

  SET_LAST_POLLING_TIME(state, time) {
    state.lastPollingTime = time;
  },

  SET_WITHDRAWAL_GROUPS(state, groupData) {
    Vue.set(state, 'withdrawalGroups', groupData || { date: null, withdrawals: [] });
  },

  SET_WITHDRAWAL_GROUP_DATA(state, groupData) {
    Vue.set(state, 'withdrawalGroupData', groupData || []);
  },

  RESET_INDICATOR_CARD(state) {
    state.indicatorCard = initialIndicatorCard();
  },

  SET_INDICATOR_CARD(state, payload) {
    state.indicatorCard = payload;
  },

  RESET_SELLER_CARD(state) {
    state.sellerCard = initialSellerCard();
  },

  SET_SELLER_CARD(state, payload) {
    state.sellerCard = payload;
  },

  RESET_SALE_HISTORY_FILTERS(state) {
    state.saleHistoryFilters = initialSaleHistoryFilters();
  },

  SET_SALE_HISTORY_FILTERS(state, payload) {
    if (!state.saleHistoryFilters) {
      state.saleHistoryFilters = {};
    }
    state.saleHistoryFilters = {
      ...state.saleHistoryFilters,
      [payload.key]: payload.value
    };
  },

  SET_REVIEW_WITHDRAWAL_TAB(state, payload) {
    state.reviewWithdrawalTab = payload;
  },

  addActiveIndicatorCardId(state, cardId) {
    if (!state.activeIndicatorCardIds.includes(cardId)) {
      state.activeIndicatorCardIds.push(cardId);
    }
  },

  removeActiveIndicatorCardId(state, cardId) {
    const index = state.activeIndicatorCardIds.indexOf(cardId);
    if (index > -1) {
      state.activeIndicatorCardIds.splice(index, 1);
    }
  },

  clearActiveIndicatorCardIds(state) {
    state.activeIndicatorCardIds = [];
  },

  addActiveFilterCardId(state, cardId) {
    if (!state.activeFilterCardIds.includes(cardId)) {
      state.activeFilterCardIds.push(cardId);
    }
  },

  removeActiveFilterCardId(state, cardId) {
    const index = state.activeFilterCardIds.indexOf(cardId);
    if (index > -1) {
      state.activeFilterCardIds.splice(index, 1);
    }
  },

  clearActiveFilterCardIds(state) {
    state.activeFilterCardIds = [];
  },

  // Grupos
  setGroupLoading(state, loading)   { state.groupLoading = loading },
  setGroupError(state, messageError)   { state.groupError = messageError },
  SET_GROUPS(state, groups)     { state.groups = groups },
  SET_GROUP_PAGINATION(state, payload) {
    state.groupPagination = {
      ...state.groupPagination,
      ...payload
    };
  },
  // Itens de grupo
  SET_ITEMS_LOADING(state, loading)   { state.withdrawalItemsLoading = loading },
  SET_ITEMS_ERROR(state, messageError)   { state.withdrawalItemsError = messageError },
  SET_ITEMS_FOR_DATE(state, { date, items }) {
    Vue.set(state.withdrawalItemsByDate, date, items);
  },

  SET_LOADING_WITHDRAWALS_ANALYSIS(state, status){
    state.loadingWithdrawalsAnalysis = status;
  },

  APPEND_ITEMS_FOR_DATE(state, { date, items })  {
    const existing = state.withdrawalItemsByDate[date] || []
    const existingIds = new Set(existing.map(i => Number(i.withdrawal_id)))
    const newItems = items.filter(i => !existingIds.has(Number(i.withdrawal_id)))

    Vue.set(
      state.withdrawalItemsByDate,
      date,
      existing.concat(newItems)
    )
  },

  RESET_ALL_WITHDRAWAL_ITEMS(state) {
    Vue.set(state, 'withdrawalItemsByDate', {});
  },

  SET_STATUSES(state, statuses) {
    state.statusOptions = statuses;
  },
}

const actions = {
  async fetchWithdrawals({ commit, state }) {
    commit('SET_LOADING', true);

    try {
      const {
        seller_name,
        seller_email,
        seller_document,
        status,
        currencyId,
        currentPage,
        perPage
      } = {
        ...state.filters,
        currentPage: state.pagination.currentPage,
        perPage:     state.pagination.perPage
      }

      const name     = seller_name;
      const email    = seller_email;
      const document = seller_document;

      const params = {
        page:        currentPage,
        per_page:    perPage,
        currency_id: currencyId,
      }
      if (status !== null) {
        params.status = status;
      } else if (state.filters.status) {
        params.status = state.filters.status;
      }
      if (name)     params.name     = name
      if (email)    params.email    = email
      if (document) params.document = document

      const response = await service.search(params)

      commit('SET_WITHDRAWALS', response.data)
      commit('SET_PAGINATION',  { totalRows: response.meta.total })

    } catch (error) {

      console.error(error);
      commit('SET_ERROR', 'Erro ao carregar saques')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchWithdrawalGroup({ commit, state }, { date, status = null }) {
    commit('SET_LOADING', true);

    try {
      const params = {
        date,
        currency_id: state.filters.currencyId,
      };

      if (status) params.status = status;

      const response = await service.getGroupItems(new URLSearchParams(params).toString());

      const groupData = response.data || response;

      commit('SET_WITHDRAWAL_GROUPS', groupData);

      return groupData;
    } catch (error) {
      console.error("Erro ao carregar grupo:", error);
      commit('SET_ERROR', "Erro ao carregar grupo");
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async setUserIdAction({ commit }, id) {
    commit('SET_USER_ID', id);
  },

  async fetchWithdrawalHistory({ commit, state }) {
    commit('SET_LOADING_HISTORY', true);
    commit('SET_WITHDRAWAL_HISTORY_ERROR', false);
    try {
      const response = await historyService.read({
        id: `/history/${state.userId}`
      });

      commit('SET_WITHDRAWAL_HISTORY', response || response.data);
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
      commit('SET_ERROR', "Erro ao carregar histórico");
      commit('SET_WITHDRAWAL_HISTORY_ERROR', true);
      commit('SET_WITHDRAWAL_HISTORY', []);
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

  updateFilter({ commit, dispatch }, { filter, value }) {
    commit('SET_FILTER', { filter, value });
    dispatch('fetchGroups');
  },

  updateMultipleFilters({ commit, dispatch }, filters) {
    for (const [filter, value] of Object.entries(filters)) {
      commit('SET_FILTER', { filter, value });
    }

    dispatch('fetchGroups');
  },

  updatePagination({ commit, dispatch }, { key, value, blockedSearch = false }) {
    commit('SET_PAGINATION', { [key]: value });

    if (blockedSearch) {
      dispatch('fetchWithdrawals');
    }
  },

  updateGroupPagination({ commit, dispatch }, { key, value, blockedSearch = false }) {
    commit('SET_GROUP_PAGINATION', { [key]: value });
    if (!blockedSearch) {
      dispatch('fetchGroups');
    }
  },

  async updateWithdrawalStatus({ commit }, { id, status, operator_id = null }) {
    commit('SET_LOADING', true);

    try {
      const payload = { status };
      if (operator_id) {
        payload.operator_id = operator_id;
      }

      const response = await service.update(payload, id);

      return response.data || response;
    } catch (error) {
      console.error("Erro ao atualizar saque:", error);

      const errorMessage =
        (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
        error.message ||
        error.error ||
        "Erro ao atualizar status";

      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async checkWithdrawalStatuses({ commit, state }, { withdrawalIds, date = null, status = null }) {
    try {
      const {
        name,
        email,
        document,
        seller_id,
        currency_id,
        status: filterStatus,
      } = state.filters;
      const response = await service.checkStatus({
        ids: withdrawalIds,
        date,
        status: status || filterStatus || null,
        name,
        email,
        document,
        seller_id,
        currency_id,
      });

      const groupData = Array.isArray(response.data) ? response.data[0] : response.data || response;

      commit('SET_WITHDRAWAL_GROUP_DATA', groupData);

      commit('SET_ITEMS_FOR_DATE', {
        date:      groupData.date,
        items:     groupData.withdrawals || []
      })

      return groupData;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      const errorMessage = (
        error.response
        && error.response.data
        && error.response.data.message
      ) || "Erro ao verificar status";
      commit('SET_ERROR', errorMessage);
    }
  },

  async exportWithdrawals({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await service.blob({
        date:       payload.date,
        currency:   payload.currency,
        new_rules:  payload.new_rules,
        provider:   payload.provider
      });
      return response || response.data;

    } catch (error) {
      let errorMessage = 'Não foi possível exportar os saques';
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errObj = JSON.parse(text);
          errorMessage = errObj.msg || errObj.message || errorMessage;
        } catch (parseErr) {
          console.error('Falha ao converter Blob de erro em texto:', parseErr);
        }
      }

      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  selectIndicatorCardAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_INDICATOR_CARD');
    }
    commit('SET_INDICATOR_CARD', payload);
  },

  selectSellerCardAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_SELLER_CARD');
    }
    commit('SET_SELLER_CARD', payload);
  },

  updateSaleHistoryFilterAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_SALE_HISTORY_FILTERS');
    }
    commit('SET_SALE_HISTORY_FILTERS', payload);
  },

  updateReviewWithdrawalTabAction({ commit }, payload) {
    commit('SET_REVIEW_WITHDRAWAL_TAB', payload);
  },

  addActiveIndicatorCardIdAction({ commit }, cardId) {
    commit('addActiveIndicatorCardId', cardId);
  },

  removeActiveIndicatorCardIdAction({ commit }, cardId) {
    commit('removeActiveIndicatorCardId', cardId);
  },

  clearActiveIndicatorCardIdsAction({ commit }) {
    commit('clearActiveIndicatorCardIds');
  },

  addActiveFilterCardIdAction({ commit }, cardId) {
    commit('addActiveFilterCardId', cardId);
  },

  removeActiveFilterCardIdAction({ commit }, cardId) {
    commit('removeActiveFilterCardId', cardId);
  },

  clearActiveFilterCardIdsAction({ commit }) {
    commit('clearActiveFilterCardIds');
  },

  async fetchGroups({ commit, state }, statusArg = null) {
    commit('setGroupLoading', true)
    commit('setGroupError', null)
    commit('RESET_ALL_WITHDRAWAL_ITEMS');
    
    try {
      const { name, email, document, status, seller_id, analyst_name } = state.filters

      const params = {
        page:       state.groupPagination.currentPage || 1,
        per_page:   state.groupPagination.perPage    || 5,
        currency_id: state.filters.currencyId        || 1
      }

      if (name)     params.name     = name
      if (email)    params.email    = email
      if (document) params.document = document
      if (seller_id) params.seller_id = seller_id
      if (analyst_name) params.analyst_name = analyst_name
      if (statusArg) {
        params.status = statusArg
      } else if (status) {
        params.status = status
      }

      const response = await service.groups(new URLSearchParams(params).toString())
      const payload = response.meta ? response : response.data
      const groups = payload.data
      const meta   = payload.meta

      commit('SET_GROUPS', groups)
      commit('SET_GROUP_PAGINATION', {
        currentPage: meta.page,
        perPage:     meta.per_page,
        totalRows:   meta.total,
        lastPage:    meta.last_page,
        hasMore:     meta.has_more,
      })
      return groups
    }
    catch (err) {
      console.error('Erro ao carregar grupos:', err)
      commit('setGroupError', 'Falha ao carregar grupos')
      throw err
    }
    finally {
      commit('setGroupLoading', false)
    }
  },

  async fetchGroupItems({ commit, state }, { date, statusArg = null }) {
    commit('SET_ITEMS_LOADING', true)
    commit('SET_ITEMS_ERROR', null)

    try {
      const { name, email, document, status, seller_id, analyst_name } = state.filters
      const params = {
        date,
        limit: 50
      }

      if (name)     params.name     = name
      if (email)    params.email    = email
      if (document) params.document = document
      if (seller_id) params.seller_id = seller_id
      if (analyst_name) params.analyst_name = analyst_name
      if (statusArg) {
        params.status = statusArg
      } else if (status) {
        params.status = status
      }

      const response = await service.getGroupWithdrawals(new URLSearchParams(params).toString())

      if (!response || !response.data) {
        throw new Error("Resposta inválida da API")
      }

      const payload = response.data
      const items = payload.withdrawals ? payload.withdrawals : []
      const respDate = payload.date ? payload.date : date

      commit('SET_ITEMS_FOR_DATE', { date: respDate, items })
      return { date: respDate, items }
    }
    catch (err) {
      console.error(`Erro ao carregar itens do grupo ${date}:`, err)
      commit('SET_ITEMS_ERROR', 'Falha ao carregar itens')
      throw err
    }
    finally {
      commit('SET_ITEMS_LOADING', false)
    }
  },

  async fetchStatuses({ commit }) {
    try {
      const response = await service.getStatuses();
      commit('SET_STATUSES', response || []);
      return response;
    } catch (err) {
      console.error('Erro ao buscar status:', err);
      return [];
    }
  },

  async getGroupPaginated({ commit }, { date, last_id = null, status = null, search = null, currency_id = null, limit = 50 , sortKey = '', sortDirection = '', lastSortValue = '' }) {
    commit('SET_LOADING_WITHDRAWALS_ANALYSIS', true)
    try {
      const params = new URLSearchParams()
      params.append('date', date)
      params.append('limit', limit)
      if (last_id)     params.append('last_id', last_id)
      if (status)      params.append('status', status)
      if (search)      params.append('search', search)
      if (currency_id) params.append('currency_id', currency_id)
      if (sortKey)     params.append('sortKey', sortKey)
      if(sortDirection)params.append('sortDirection', sortDirection)
      if(lastSortValue)params.append('lastSortValue', lastSortValue)

      const response = await service.getGroupWithdrawals(params.toString())
      const { date: respDate, withdrawals } = response.data

      commit('APPEND_ITEMS_FOR_DATE', { date: respDate, items: withdrawals })

      return { date: respDate, withdrawals }
    } catch (err) {
      console.error('Erro ao carregar mais itens:', err)
      throw err
    } finally {
      commit('SET_LOADING_WITHDRAWALS_ANALYSIS', false)
    }
  },

  async fetchWithdrawalSalesFiltered({ commit, state }) {
    commit('SET_LOADING_HISTORY', true);
    try {
      const withdrawalId = state.selectedWithdrawalId;
      const rawFilters = state.saleHistoryFilters;

      const filters = {
        page: 1,
        per_page: 10,
        ...Object.entries(rawFilters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[`filter[${key}]`] = value;
          }
          return acc;
        }, {})
      };

      const response = await service.getSalesWithFilters(withdrawalId, filters);

      commit('setWithdrawalSales', response.data || []);
    } catch (error) {
      console.error("Erro ao buscar vendas filtradas:", error);
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

}

export {
  state,
  getters,
  mutations,
  actions
}
